import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { api } from "../../api"
import { TimeZoneType } from "../../constants/timezones"
import { OptionType, TaskResponse } from "../../tasks/api/task-response"
import { Status } from "../../types/common"
import { ProjectPermissionRecord } from "./project-permission-response"
import { ProjectResponse } from "./project-response"
import { ProjectStateType, ProjectType } from "../types"
import { makeProjectApiEndpoints } from "./endpoints"
import { ProjectViewModel, makeProjectViewModel } from "./project"
import { TaskViewModel, makeTaskViewModel } from "../../tasks/api/task"
import { makeUrlSearchParams } from "../../utils/url"
import { NotificationChannel, NotificationType } from "../../notifications/notifications"
import { TreeGridColors } from "../../treegrid/types"
import { makeProjectDashboardViewModel, ProjectDashboardViewModel } from "./project-dashboard"
import { makeProjectsDashboardViewModel, ProjectsDashboardViewModel } from "./projects-dashboard"
import { hasPendingMutations } from "../../utils/rtk-query-util"
import { DateTimeService } from "../../services/date-time-service"

const apiEndpoints = makeProjectApiEndpoints()
const pendingEndpointNames = [
  "addProjectManagers",
  "removeProjectManagers",
  "addProjectParticipants",
  "removeProjectParticipants",
  "addProjectCustomers",
  "removeProjectCustomers",
  "addProjectSuppliers",
  "removeProjectSuppliers",
  "addProjectWorkspaces",
  "removeProjectWorkspaces",
]
export const projectApi = api.injectEndpoints({
  endpoints: (builder) => {
    return {
      getProjectById: builder.query<ProjectViewModel, string>({
        query: apiEndpoints.projectItem,
        transformResponse: ({ project }: { project: ProjectResponse }) => makeProjectViewModel(project),
        providesTags: (result) => [{ type: "Project", id: result?.id }],
      }),
      getProjectDashboard: builder.query<ProjectDashboardViewModel, string>({
        query: apiEndpoints.projectDashboard,
        transformResponse: ({ dashboard }) => makeProjectDashboardViewModel(dashboard),
        providesTags: (result) => [{ type: "ProjectDashboard", id: result?.id }],
      }),
      getProjectsDashboard: builder.query<ProjectsDashboardViewModel, ProjectsDashboardQueryParams | void>({
        query: (params) => {
          return {
            method: "GET",
            url: apiEndpoints.projectsDashboard(),
            params: params ? makeUrlSearchParams(params) : undefined,
          }
        },
        transformResponse: ({ projectsDashboard }) => makeProjectsDashboardViewModel(projectsDashboard),
        providesTags: (result) => [{ type: "ProjectsDashboard", id: result?.id }],
      }),
      getProjects: builder.query<ProjectViewModel[], ProjectsQueryParams | void>({
        query: (params) => {
          return {
            method: "GET",
            url: apiEndpoints.projects(),
            params: params ? makeUrlSearchParams(params) : undefined,
          }
        },
        transformResponse: (response: { projects: ProjectResponse[] }) => {
          return response.projects.map(makeProjectViewModel)
        },
        providesTags: (result = []) => ["Project", ...result.map(({ id }) => ({ type: "Project", id } as const))],
      }),
      getProjectPermissions: builder.query<ProjectPermissionRecord[], string>({
        query: apiEndpoints.projectPermissions,
        transformResponse: (response: ProjectPermissionRecord[]) => response,
        providesTags: (result, error, id) => ["ProjectPermission", { type: "ProjectPermission", id }],
      }),
      createUserProject: builder.mutation<ProjectViewModel, { userId: string } & NewProjectData>({
        query: ({ userId, ...projectData }) => ({
          url: apiEndpoints.userProjects(userId),
          method: "POST",
          body: projectData,
        }),
        transformResponse: makeProjectViewModel,
        invalidatesTags: ["Project"],
      }),
      createOrgProject: builder.mutation<ProjectViewModel, { orgId: string } & NewProjectData>({
        query: ({ orgId, ...projectData }) => ({
          url: apiEndpoints.orgProjects(orgId),
          method: "POST",
          body: projectData,
        }),
        transformResponse: makeProjectViewModel,
        transformErrorResponse: (error) => transformError(error, "Failed to create a new project"),
        invalidatesTags: ["Project", "Chat"],
      }),
      createProjectFromTemplate: builder.mutation<ProjectViewModel, CreateProjectFromTemplateData>({
        query: (projectData) => ({
          url: apiEndpoints.createProjectFromTemplate(),
          method: "POST",
          body: projectData,
        }),
        transformResponse: makeProjectViewModel,
        transformErrorResponse: (error) => transformError(error, "Failed to create a new project"),
        invalidatesTags: ["Project"],
      }),
      updateProjectInfo: builder.mutation<ProjectViewModel, { projectId: string } & ProjectInfoUpdateData>({
        query: ({ projectId, ...projectData }) => ({
          url: apiEndpoints.projectItem(projectId),
          method: "PUT",
          body: projectData,
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, ...projectData }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              Object.assign(draft, projectData)
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: "updateProjectInfo" }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }, "Chat"]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      updateProjectNumber: builder.mutation<
        { project: ProjectViewModel; tasks: TaskViewModel[] },
        { projectId: string } & ProjectNumberUpdateData
      >({
        query: ({ projectId, ...projectData }) => ({
          url: apiEndpoints.projectNumber(projectId),
          method: "PUT",
          body: projectData,
        }),
        transformResponse: (response: { project: ProjectResponse; tasks: TaskResponse[] }) => {
          return {
            project: makeProjectViewModel(response.project),
            tasks: response.tasks.map((task) => makeTaskViewModel(task)),
          }
        },
        invalidatesTags: (result) => ["Project", "Task", { type: "Project", id: result?.project.id }],
      }),
      updateProjectPlan: builder.mutation<ProjectViewModel, { projectId: string } & ProjectPlanUpdateData>({
        query: ({ projectId, ...projectData }) => ({
          url: apiEndpoints.projectPlan(projectId),
          method: "PUT",
          body: projectData,
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, ...projectData }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              const nextStart = projectData.plannedStartDate ?? draft.plannedStartDate
              const nextEnd = projectData.plannedEndDate ?? draft.plannedEndDate
              if (nextStart && nextEnd) {
                if (nextEnd < nextStart) {
                  return
                } else if (DateTimeService.validateProjectDuration(nextStart, nextEnd)) {
                  Object.assign(draft, projectData)
                }
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: "updateProjectPlan" }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      updateProjectStatus: builder.mutation<
        { project: ProjectViewModel; tasks: TaskViewModel[] },
        { projectId: string } & ProjectStatusUpdateData
      >({
        query: ({ projectId, ...projectData }) => ({
          url: apiEndpoints.projectStatus(projectId),
          method: "PUT",
          body: projectData,
        }),
        transformResponse: (response: { project: ProjectResponse; tasks: TaskResponse[] }) => {
          return {
            project: makeProjectViewModel(response.project),
            tasks: response.tasks.map((task) => makeTaskViewModel(task)),
          }
        },
        onQueryStarted: async ({ projectId, ...projectData }, { dispatch, queryFulfilled, getState }) => {
          const optimisticUpdateData = {
            ...projectData,
            customStatus: projectData.customStatus || projectData.status,
          }
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (optimisticUpdateData.status) {
                draft.status = optimisticUpdateData.status
                draft.customStatus = optimisticUpdateData.customStatus || optimisticUpdateData.status
                draft.subStatusId = optimisticUpdateData.subStatusId
                // If the project is being set to Not Started, we should clear actual start and end dates
                if (draft.status === Status.NOT_STARTED) {
                  draft.actualStartDate = ""
                  draft.actualEndDate = ""
                }
              } else if (
                "actualStartDate" in optimisticUpdateData &&
                optimisticUpdateData.actualStartDate !== undefined
              ) {
                draft.actualStartDate = optimisticUpdateData.actualStartDate
                if (draft.status !== Status.REJECTED && draft.status !== Status.COMPLETED) {
                  // If the actual start date is being cleared,
                  // we should set status to Not Started and clear actual end date.
                  // Otherwise, if there is an actual start date, we should set status to In Progress
                  // and update custom status to the one provided in the optimistic update data or keep it unchanged if not provided
                  if (optimisticUpdateData.actualStartDate === "") {
                    draft.status = Status.NOT_STARTED
                    draft.customStatus = Status.NOT_STARTED
                    draft.actualEndDate = ""
                  } else {
                    draft.status = Status.IN_PROGRESS
                    draft.customStatus = optimisticUpdateData.customStatus || draft.customStatus || Status.IN_PROGRESS
                    draft.subStatusId = optimisticUpdateData.subStatusId || draft.subStatusId
                  }
                }
              } else if ("actualEndDate" in optimisticUpdateData && optimisticUpdateData.actualEndDate !== undefined) {
                draft.actualEndDate = optimisticUpdateData.actualEndDate
                if (draft.status !== Status.REJECTED) {
                  // If the actual end date is being cleared,
                  // we should set status to In Progress
                  // and if actual end date is being changed/set to a non-empty value,
                  // we should set status to Completed and update custom status to the one provided
                  if (optimisticUpdateData.actualEndDate !== "") {
                    draft.status = Status.COMPLETED
                    draft.customStatus = optimisticUpdateData.customStatus || draft.customStatus || Status.COMPLETED
                    draft.subStatusId = optimisticUpdateData.subStatusId || draft.subStatusId
                    if (draft.actualStartDate === "") {
                      draft.actualStartDate = optimisticUpdateData.actualEndDate
                    }
                  } else {
                    draft.status = Status.IN_PROGRESS
                    draft.customStatus = Status.IN_PROGRESS
                  }
                }
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: "updateProjectStatus" }))
              dispatch(projectApi.util.invalidateTags(["Project", "Task", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      addProjectManagers: builder.mutation<ProjectViewModel, { projectId: string; managers: OptionType[] }>({
        query: ({ projectId, managers }) => ({
          url: apiEndpoints.projectManagers(projectId),
          method: "POST",
          body: { managerIds: managers.map((manager) => manager.id) },
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, managers }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (!draft.managers) draft.managers = []
              draft.managers.push(...managers)
              const managerIds = draft.managers?.map((manager) => manager.id) || []
              draft.participants = draft.participants.filter((p) => !managerIds.includes(p.id))
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (
              !hasPendingMutations(state, {
                endpointNames: pendingEndpointNames,
              })
            )
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }, "Chat"]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      removeProjectManagers: builder.mutation<ProjectViewModel, { projectId: string; managers: OptionType[] }>({
        query: ({ projectId, managers }) => ({
          url: apiEndpoints.projectManagers(projectId),
          method: "DELETE",
          body: { managerIds: managers.map((manager) => manager.id) },
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, managers }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (draft.managers) {
                draft.managers = draft.managers.filter(
                  (manager) => !managers.some((remove) => remove.id === manager.id)
                )
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }, "Chat"]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      addProjectParticipants: builder.mutation<ProjectViewModel, { projectId: string; participants: OptionType[] }>({
        query: ({ projectId, participants }) => ({
          url: apiEndpoints.projectParticipants(projectId),
          method: "POST",
          body: { participantIds: participants.map((participant) => participant.id) },
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, participants }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (!draft.participants) draft.participants = []
              draft.participants.push(...participants)
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }, "Chat"]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      removeProjectParticipants: builder.mutation<ProjectViewModel, { projectId: string; participants: OptionType[] }>({
        query: ({ projectId, participants }) => ({
          url: apiEndpoints.projectParticipants(projectId),
          method: "DELETE",
          body: { participantIds: participants.map((participant) => participant.id) },
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, participants }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (draft.participants) {
                draft.participants = draft.participants.filter(
                  (participant) => !participants.some((remove) => remove.id === participant.id)
                )
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }, "Chat"]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      addProjectCustomers: builder.mutation<
        { project: ProjectViewModel; tasks: TaskViewModel[] },
        { projectId: string; customers: OptionType[] }
      >({
        query: ({ projectId, customers }) => ({
          url: apiEndpoints.projectCustomers(projectId),
          method: "POST",
          body: { customerIds: customers.map((customer) => customer.id) },
        }),
        transformResponse: (response: { project: ProjectResponse; tasks: TaskResponse[] }) => {
          return {
            project: makeProjectViewModel(response.project),
            tasks: response.tasks.map((task) => makeTaskViewModel(task)),
          }
        },
        onQueryStarted: async ({ projectId, customers }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (!draft.customers) draft.customers = []
              draft.customers.push(...customers)
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", "Task", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      removeProjectCustomers: builder.mutation<
        { project: ProjectViewModel; tasks: TaskViewModel[] },
        { projectId: string; customers: OptionType[] }
      >({
        query: ({ projectId, customers }) => ({
          url: apiEndpoints.projectCustomers(projectId),
          method: "DELETE",
          body: { customerIds: customers.map((customer) => customer.id) },
        }),
        transformResponse: (response: { project: ProjectResponse; tasks: TaskResponse[] }) => {
          return {
            project: makeProjectViewModel(response.project),
            tasks: response.tasks.map((task) => makeTaskViewModel(task)),
          }
        },
        onQueryStarted: async ({ projectId, customers }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (draft.customers) {
                draft.customers = draft.customers.filter(
                  (customer) => !customers.some((remove) => remove.id === customer.id)
                )
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", "Task", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      addProjectSuppliers: builder.mutation<ProjectViewModel, { projectId: string; suppliers: OptionType[] }>({
        query: ({ projectId, suppliers }) => ({
          url: apiEndpoints.projectSuppliers(projectId),
          method: "POST",
          body: { supplierIds: suppliers.map((supplier) => supplier.id) },
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, suppliers }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (!draft.suppliers) draft.suppliers = []
              draft.suppliers.push(...suppliers)
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      removeProjectSuppliers: builder.mutation<ProjectViewModel, { projectId: string; suppliers: OptionType[] }>({
        query: ({ projectId, suppliers }) => ({
          url: apiEndpoints.projectSuppliers(projectId),
          method: "DELETE",
          body: { supplierIds: suppliers.map((supplier) => supplier.id) },
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, suppliers }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (draft.suppliers) {
                draft.suppliers = draft.suppliers.filter(
                  (supplier) => !suppliers.some((remove) => remove.id === supplier.id)
                )
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      addProjectWorkspaces: builder.mutation<ProjectViewModel, { projectId: string; workspaces: OptionType[] }>({
        query: ({ projectId, workspaces }) => ({
          url: apiEndpoints.projectWorkspaces(projectId),
          method: "POST",
          body: { workspaceIds: workspaces.map((workspace) => workspace.id) },
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, workspaces }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (!draft.workspaces) draft.workspaces = []
              draft.workspaces.push(...workspaces)
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      removeProjectWorkspaces: builder.mutation<ProjectViewModel, { projectId: string; workspaces: OptionType[] }>({
        query: ({ projectId, workspaces }) => ({
          url: apiEndpoints.projectWorkspaces(projectId),
          method: "DELETE",
          body: { workspaceIds: workspaces.map((workspace) => workspace.id) },
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, workspaces }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              if (draft.workspaces) {
                draft.workspaces = draft.workspaces.filter(
                  (workspace) => !workspaces.some((remove) => remove.id === workspace.id)
                )
              }
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      saveBasePlan: builder.mutation<{ project: ProjectViewModel; tasks: TaskViewModel[] }, string>({
        query: (projectId) => ({
          url: apiEndpoints.projectBasePlan(projectId),
          method: "PUT",
        }),
        transformResponse: (response: { project: ProjectResponse; tasks: TaskResponse[] }) => {
          return {
            project: makeProjectViewModel(response.project),
            tasks: response.tasks.map((task) => makeTaskViewModel(task)),
          }
        },
        invalidatesTags: (result) => ["Project", "Task", { type: "Project", id: result?.project.id }],
      }),
      updateTimeComponent: builder.mutation<ProjectViewModel, { projectId: string } & ProjectTimeComponentUpdateData>({
        query: ({ projectId, ...updateData }) => ({
          url: apiEndpoints.projectTimeComponent(projectId),
          method: "PUT",
          body: updateData,
        }),
        transformResponse: makeProjectViewModel,
        invalidatesTags: (result) => ["Project", { type: "Project", id: result?.id }],
      }),
      updateProjectStatusDescription: builder.mutation<
        ProjectViewModel,
        { projectId: string } & StatusDescriptionUpdateData
      >({
        query: ({ projectId, ...updateData }) => ({
          url: apiEndpoints.projectStatusDescription(projectId),
          method: "PUT",
          body: updateData,
        }),
        transformResponse: makeProjectViewModel,
        onQueryStarted: async ({ projectId, ...updateData }, { dispatch, queryFulfilled, getState }) => {
          const patchResult = dispatch(
            projectApi.util.updateQueryData("getProjectById", projectId, (draft) => {
              Object.assign(draft, updateData)
            })
          )
          try {
            await queryFulfilled
            const state = getState()
            if (!hasPendingMutations(state, { endpointNames: "updateProjectStatusDescription" }))
              dispatch(projectApi.util.invalidateTags(["Project", { type: "Project", id: projectId }]))
          } catch {
            patchResult.undo()
          }
        },
      }),
      updateProjectPermissions: builder.mutation<
        ProjectPermissionRecord[],
        { projectId: string } & ProjectPermissionUpdateData
      >({
        query: ({ projectId, membershipIds, abilitiesToAdd = [], abilitiesToRemove = [] }) => ({
          url: apiEndpoints.projectPermissions(projectId),
          method: "PUT",
          body: { membershipIds, abilitiesToAdd, abilitiesToRemove },
        }),
        transformResponse: (response: ProjectPermissionRecord[]) => response,
        invalidatesTags: (result, error, { projectId }) => [{ type: "ProjectPermission", id: projectId }],
      }),
      archiveProject: builder.mutation<{ project: ProjectViewModel; tasks: TaskViewModel[] }, string>({
        query: (projectId) => ({
          url: apiEndpoints.archiveProject(projectId),
          method: "PATCH",
        }),
        transformResponse: (response: { project: ProjectResponse; tasks: TaskResponse[] }) => {
          return {
            project: makeProjectViewModel(response.project),
            tasks: response.tasks.map((task) => makeTaskViewModel(task)),
          }
        },
        invalidatesTags: (result) => ["Project", "Task", { type: "Project", id: result?.project.id }],
      }),
      unarchiveProject: builder.mutation<{ project: ProjectViewModel; tasks: TaskViewModel[] }, string>({
        query: (projectId) => ({
          url: apiEndpoints.unarchiveProject(projectId),
          method: "PATCH",
        }),
        transformResponse: (response: { project: ProjectResponse; tasks: TaskResponse[] }) => {
          return {
            project: makeProjectViewModel(response.project),
            tasks: response.tasks.map((task) => makeTaskViewModel(task)),
          }
        },
        invalidatesTags: (result) => ["Project", "Task", { type: "Project", id: result?.project.id }],
      }),
      deleteProject: builder.mutation<boolean, string>({
        query: (projectId) => ({
          url: apiEndpoints.projectItem(projectId),
          method: "DELETE",
        }),
        transformResponse: () => true,
        onQueryStarted: async (projectId, { dispatch, queryFulfilled }) => {
          await queryFulfilled
          setTimeout(() => {
            dispatch(
              projectApi.util.invalidateTags([
                "Project",
                "Task",
                "CalendarEvent",
                "Todo",
                { type: "Board", id: projectId },
                { type: "Project", id: projectId },
              ])
            )
          }, 1000)
        },
      }),
      updateProjectNotificationSettings: builder.mutation<
        ProjectViewModel,
        { projectId: string } & ProjectNotificationSettingsUpdateData
      >({
        query: ({ projectId, ...updateData }) => ({
          url: apiEndpoints.projectNotificationSettings(projectId),
          method: "PUT",
          body: updateData,
        }),
        invalidatesTags: ["Project"],
      }),
      updateProjectCompletionPercentage: builder.mutation<
        ProjectViewModel,
        { projectId: string } & ProjectCompletionPercentageUpdateData
      >({
        query: ({ projectId, ...updateData }) => ({
          url: apiEndpoints.updateCompletionPercentage(projectId),
          method: "PUT",
          body: updateData,
        }),
        transformResponse: makeProjectViewModel,
        invalidatesTags: (result) => ["Project", { type: "Project", id: result?.id }],
      }),
    }
  },
})

export const {
  useGetProjectByIdQuery,
  useGetProjectDashboardQuery,
  useGetProjectsDashboardQuery,
  useGetProjectsQuery,
  useGetProjectPermissionsQuery,
  useCreateUserProjectMutation,
  useCreateOrgProjectMutation,
  useUpdateProjectInfoMutation,
  useUpdateProjectNumberMutation,
  useUpdateProjectPlanMutation,
  useUpdateProjectStatusMutation,
  useAddProjectManagersMutation,
  useRemoveProjectManagersMutation,
  useAddProjectParticipantsMutation,
  useRemoveProjectParticipantsMutation,
  useAddProjectCustomersMutation,
  useRemoveProjectCustomersMutation,
  useAddProjectSuppliersMutation,
  useRemoveProjectSuppliersMutation,
  useAddProjectWorkspacesMutation,
  useRemoveProjectWorkspacesMutation,
  useSaveBasePlanMutation,
  useUpdateTimeComponentMutation,
  useUpdateProjectStatusDescriptionMutation,
  useUpdateProjectPermissionsMutation,
  useArchiveProjectMutation,
  useUnarchiveProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectNotificationSettingsMutation,
  useCreateProjectFromTemplateMutation,
  useUpdateProjectCompletionPercentageMutation,
} = projectApi

type ProjectsBaseQuery = {
  status?: Status | Status[]
  archived?: "true" | "false"
  type?: ProjectType
  links?: "true"
  files?: "true"
}
type OrgProjectsQueryParams = { organisation: string }
type CustomerProjectsQueryParams = { customer: string }
type MemberProjectsQueryParams = { member: string }
type SupplierProjectsQueryParams = { supplier: string }
type WorkspaceProjectsQueryParams = { workspace: string }
// Expected Projects Query
export type ProjectsQueryParams = ProjectsBaseQuery &
  (
    | {}
    | OrgProjectsQueryParams
    | CustomerProjectsQueryParams
    | MemberProjectsQueryParams
    | SupplierProjectsQueryParams
    | WorkspaceProjectsQueryParams
  )
export type ProjectsDashboardQueryParams = {
  archived?: "true" | "false"
  organisation?: string
  customer?: string
  member?: string
  supplier?: string
  workspace?: string
  completedProjectsStartDate?: string
  completedProjectsEndDate?: string
  completedProjectsPeriodType?: "week" | "month" | "day"
}

export type NewProjectData = {
  title: string
  type?: ProjectType
  description?: string
  plannedStartDate?: string
  plannedEndDate?: string
  customers?: string[]
  suppliers?: string[]
  workspaces?: string[]
  managers?: string[]
}

export type CreateProjectFromTemplateData = {
  title: string
  templateId: string
  type?: ProjectType
  description?: string
  plannedStartDate?: string
  plannedEndDate?: string
  customers?: string[]
  suppliers?: string[]
  workspaces?: string[]
  managers?: string[]
}

export type ProjectInfoUpdateData = {
  title?: string
  description?: string
  timeZone?: TimeZoneType
  weekendDays?: DayOfWeek[]
  firstDayOfWeek?: DayOfWeek
  ganttBarColor?: TreeGridColors
  enableHolidays?: boolean
}

export type ProjectNumberUpdateData = {
  customProjectNumber: string
}

export type ProjectPlanUpdateData = {
  plannedStartDate?: string
  plannedEndDate?: string
  lockPlannedStartDate?: boolean
  lockPlannedEndDate?: boolean
}

export type ProjectStatusUpdateData = {
  actualStartDate?: string
  actualEndDate?: string
  status?: Status
  subStatusId?: string | null
  updateProjectTasks?: boolean
  projectTypeId?: string | null
  customStatus?: string | null
}

export type ProjectTimeComponentUpdateData = {
  enableTimeComponent: boolean
}

export type StatusDescriptionUpdateData = {
  state?: ProjectStateType
  statusDescription?: string | null
}

export type ProjectPermissionUpdateData = {
  membershipIds: string[]
  abilitiesToAdd?: string[]
  abilitiesToRemove?: string[]
}

function transformError(error: FetchBaseQueryError, defaultMessage = "Unknown action error") {
  if ("data" in error) {
    try {
      // @ts-expect-error
      return JSON.parse(error.data)
    } catch (error) {
      return { message: defaultMessage }
    }
  }
}

type NotificationUpdateAction = { type: NotificationType; channel: NotificationChannel }
export type ProjectNotificationSettingsUpdateData = {
  enable: NotificationUpdateAction[]
  disable: NotificationUpdateAction[]
}

export type ProjectCompletionPercentageUpdateData = {
  completionPercentage: number
}
